import { useState } from 'react'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'

interface AgentRow {
  agentRaw: string
  knownAgent: string | null
  isKnown: boolean
  totalClients: number
  withB2BStatus: number
  missingB2BStatus: number
  clients?: Array<{ name: string; regcode: string | null; status: string | null }>
}

interface PreviewData {
  ok: boolean
  summary?: Record<string, number>
  statusDistribution?: Record<string, number>
  agentColumnsDetected?: string[]
  agentNameFieldFill?: Record<string, number>
  availableKeys?: string[]
  knownAgentsCovered?: string[]
  knownAgentsMissing?: string[]
  knownAgents?: AgentRow[]
  unknownAgentValues?: AgentRow[]
  note?: string
  mode?: string
  count?: number
  sample?: Array<Record<string, unknown>>
}

export function AgentiPreviewPage() {
  const canWrite = useAuthStore((s) => s.permissions.includes('operational.agenti:write'))
  // Diagnosticul brut din EA ocoleste portofoliul — doar adminul (si serverul refuza).
  const admin = useAuthStore((s) => s.user?.role === 'admin')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<PreviewData | null>(null)
  const [copied, setCopied] = useState(false)
  const [findName, setFindName] = useState('Dr.Max S.R.L.')

  async function run(mode: 'normal' | 'full' | 'raw' | 'find') {
    setLoading(true)
    setError(null)
    setCopied(false)
    try {
      const qs =
        mode === 'full'
          ? '?full=1'
          : mode === 'raw'
            ? '?raw=3'
            : mode === 'find'
              ? `?find=${encodeURIComponent(findName)}`
              : ''
      // Aducerea celor ~68k parteneri din EA (paginat, fara cache) dureaza ~15-20s,
      // peste timeout-ul implicit de 15s al clientului → marim doar pentru acest request.
      const res = await api.get<PreviewData>(`/agents/portfolio-preview${qs}`, {
        timeout: 90_000,
      })
      setData(res.data)
    } catch (e) {
      const err = e as { response?: { data?: { error?: { message?: string } } }; message?: string }
      setError(err.response?.data?.error?.message ?? err.message ?? 'Eroare necunoscută')
    } finally {
      setLoading(false)
    }
  }

  function copyJson() {
    if (!data) return
    void navigator.clipboard.writeText(JSON.stringify(data, null, 2))
    setCopied(true)
  }

  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState<{
    created: number
    updated: number
    totalImported: number
    skippedUnknown: number
    perAgent: Record<string, number>
  } | null>(null)

  async function doImport() {
    if (
      !window.confirm(
        'Scrie în CRM clienții agenților cunoscuți (upsert în PharmaClient). Continui?',
      )
    )
      return
    setImporting(true)
    setError(null)
    setImportResult(null)
    try {
      const res = await api.post('/agents/import-portfolios', {}, { timeout: 90_000 })
      setImportResult(res.data)
    } catch (e) {
      const err = e as { response?: { data?: { error?: { message?: string } } }; message?: string }
      setError(err.response?.data?.error?.message ?? err.message ?? 'Eroare la import')
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Portofolii agenți (EA)</h1>
        <p className="text-slate-500 mt-1">
          Citește partenerii din ExpertAccounts (read-only) și arată ce agent are fiecare. Nu scrie
          nimic în baza de date.
        </p>
      </div>

      <div className="flex gap-3 items-center">
        <button
          onClick={() => run('normal')}
          disabled={loading}
          className="px-4 py-2 rounded-md bg-sky-700 text-white font-medium hover:bg-sky-800 disabled:opacity-50"
        >
          {loading ? 'Se încarcă…' : 'Previzualizează'}
        </button>
        <button
          onClick={() => run('full')}
          disabled={loading}
          className="px-4 py-2 rounded-md border border-sky-700 text-sky-700 font-medium hover:bg-sky-50 disabled:opacity-50"
        >
          Previzualizare cu lista completă
        </button>
        {admin && (
          <button
            onClick={() => run('raw')}
            disabled={loading}
            className="px-4 py-2 rounded-md border border-amber-500 text-amber-700 font-medium hover:bg-amber-50 disabled:opacity-50"
          >
            Diagnostic câmpuri (3 parteneri)
          </button>
        )}
      </div>

      <div className="flex gap-2 items-center">
        {admin && (
          <>
            <input
              value={findName}
              onChange={(e) => setFindName(e.target.value)}
              placeholder="Nume exact partener (ex. Dr.Max S.R.L.)"
              className="px-3 py-2 rounded-md border border-slate-300 text-sm w-80"
            />
            <button
              onClick={() => run('find')}
              disabled={loading}
              className="px-4 py-2 rounded-md border border-emerald-600 text-emerald-700 font-medium hover:bg-emerald-50 disabled:opacity-50"
            >
              Caută partener
            </button>
          </>
        )}
        {canWrite && (
          <button
            onClick={doImport}
            disabled={importing || loading}
            className="px-4 py-2 rounded-md bg-emerald-700 text-white font-medium hover:bg-emerald-800 disabled:opacity-50"
          >
            {importing ? 'Se importă…' : 'Importă în CRM'}
          </button>
        )}
        {data && (
          <button
            onClick={copyJson}
            className="px-3 py-2 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50"
          >
            {copied ? 'Copiat ✓' : 'Copiază JSON'}
          </button>
        )}
      </div>

      {admin && <OpenInvoicesPanel />}

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-4 py-3">
          {error}
        </div>
      )}

      {importResult && (
        <div className="rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3">
          <div className="font-semibold">
            Import gata: {importResult.totalImported} clienți ({importResult.created} noi,{' '}
            {importResult.updated} actualizați). Ignorați (agent necunoscut):{' '}
            {importResult.skippedUnknown}.
          </div>
          <div className="text-sm mt-1">
            {Object.entries(importResult.perAgent)
              .map(([a, n]) => `${a}: ${n}`)
              .join(' · ')}
          </div>
        </div>
      )}

      {data && data.mode === 'raw' && (
        <section>
          <h2 className="font-semibold text-slate-700 mb-2">
            Câmpuri native ({data.count} parteneri) — caută aici cum se cheamă agentul
          </h2>
          <pre className="text-xs bg-slate-900 text-slate-100 rounded-md p-4 overflow-auto max-h-[60vh]">
            {JSON.stringify(data.sample, null, 2)}
          </pre>
        </section>
      )}

      {data && data.mode !== 'raw' && (
        <div className="space-y-6">
          {/* Sumar */}
          {data.summary && (
            <section>
              <h2 className="font-semibold text-slate-700 mb-2">Sumar</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(data.summary).map(([k, v]) => (
                  <div key={k} className="rounded-md border border-slate-200 px-3 py-2">
                    <div className="text-xs text-slate-500">{k}</div>
                    <div className="text-lg font-bold text-slate-800">
                      {v.toLocaleString('ro-RO')}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Diagnostic coloane */}
          <section className="grid md:grid-cols-2 gap-4">
            <div>
              <h2 className="font-semibold text-slate-700 mb-2">Coloane disponibile (EA)</h2>
              <div className="text-sm bg-slate-50 border border-slate-200 rounded-md p-3 break-words">
                {data.availableKeys?.join(', ') || '—'}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Dacă lipsește <code>agentpart</code>, mai trebuie adăugat în Var2 (EA).
              </p>
            </div>
            <div>
              <h2 className="font-semibold text-slate-700 mb-2">Câmp agent — câți completați</h2>
              <table className="text-sm w-full">
                <tbody>
                  {data.agentNameFieldFill &&
                    Object.entries(data.agentNameFieldFill).map(([k, v]) => (
                      <tr key={k} className="border-b border-slate-100">
                        <td className="py-1 font-mono text-slate-600">{k}</td>
                        <td className="py-1 text-right font-semibold">
                          {v.toLocaleString('ro-RO')}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Status distribution */}
          {data.statusDistribution && (
            <section>
              <h2 className="font-semibold text-slate-700 mb-2">Distribuție pe stare</h2>
              <div className="text-sm flex flex-wrap gap-2">
                {Object.entries(data.statusDistribution).map(([k, v]) => (
                  <span key={k} className="rounded-full bg-slate-100 px-3 py-1">
                    {k}: <b>{v.toLocaleString('ro-RO')}</b>
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Agenți cunoscuți */}
          <section>
            <h2 className="font-semibold text-slate-700 mb-2">Agenți cunoscuți</h2>
            {data.knownAgentsMissing && data.knownAgentsMissing.length > 0 && (
              <p className="text-sm text-amber-700 mb-2">
                Fără clienți / negăsiți: {data.knownAgentsMissing.join(', ')}
              </p>
            )}
            <table className="w-full text-sm border border-slate-200 rounded-md overflow-hidden">
              <thead className="bg-slate-700 text-white">
                <tr>
                  <th className="text-left px-3 py-2">Agent</th>
                  <th className="text-right px-3 py-2">Clienți</th>
                  <th className="text-right px-3 py-2">Cu status B2B</th>
                  <th className="text-right px-3 py-2">Fără B2B</th>
                </tr>
              </thead>
              <tbody>
                {(data.knownAgents ?? []).map((a) => (
                  <tr key={a.agentRaw} className="border-b border-slate-100">
                    <td className="px-3 py-2">{a.knownAgent ?? a.agentRaw}</td>
                    <td className="px-3 py-2 text-right font-semibold">{a.totalClients}</td>
                    <td className="px-3 py-2 text-right">{a.withB2BStatus}</td>
                    <td className="px-3 py-2 text-right text-amber-700">{a.missingB2BStatus}</td>
                  </tr>
                ))}
                {(!data.knownAgents || data.knownAgents.length === 0) && (
                  <tr>
                    <td colSpan={4} className="px-3 py-3 text-center text-slate-400">
                      Niciun agent cunoscut găsit (verifică dacă <code>agentpart</code> e expus în
                      EA)
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>

          {/* Valori agent necunoscute */}
          {data.unknownAgentValues && data.unknownAgentValues.length > 0 && (
            <section>
              <h2 className="font-semibold text-slate-700 mb-2">
                Valori „agent” necunoscute (typo-uri / agenți vechi)
              </h2>
              <div className="text-sm flex flex-wrap gap-2">
                {data.unknownAgentValues.map((a) => (
                  <span
                    key={a.agentRaw}
                    className="rounded-md bg-amber-50 border border-amber-200 px-2 py-1"
                  >
                    {a.agentRaw}: {a.totalClients}
                  </span>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  )
}

function OpenInvoicesPanel() {
  const canWrite = useAuthStore((s) => s.permissions.includes('operational.agenti:write'))
  const [t1, setT1] = useState('')
  const [t2, setT2] = useState('')
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [summary, setSummary] = useState(false)
  const [name, setName] = useState('')
  const [raw, setRaw] = useState<unknown>(null)

  async function saveTokens() {
    setBusy(true)
    setMsg(null)
    try {
      await api.post('/agents/openinv-config', { t1: t1.trim(), t2: t2.trim() })
      setMsg('Tokenii au fost salvați ✓')
      setT1('')
      setT2('')
    } catch (e) {
      const err = e as { response?: { data?: { error?: { message?: string } } }; message?: string }
      setMsg(err.response?.data?.error?.message ?? err.message ?? 'Eroare')
    } finally {
      setBusy(false)
    }
  }

  async function runPreview(debug = false) {
    setBusy(true)
    setMsg(null)
    setRaw(null)
    try {
      const res = await api.get('/agents/openinv-preview', {
        params: {
          summary: summary ? '1' : undefined,
          name: name || undefined,
          debug: debug ? '1' : undefined,
        },
        timeout: 90_000,
      })
      setRaw(res.data)
    } catch (e) {
      const err = e as { response?: { data?: { error?: { message?: string } } }; message?: string }
      setMsg(err.response?.data?.error?.message ?? err.message ?? 'Eroare')
    } finally {
      setBusy(false)
    }
  }

  async function syncSold() {
    setBusy(true)
    setMsg(null)
    try {
      const res = await api.post('/agents/sync-sold', {}, { timeout: 120_000 })
      const d = res.data as { clientsUpdated: number; partnersFromEa: number }
      setMsg(
        `Sold sincronizat: ${d.clientsUpdated} clienți actualizați (din ${d.partnersFromEa} parteneri EA).`,
      )
    } catch (e) {
      const err = e as { response?: { data?: { error?: { message?: string } } }; message?: string }
      setMsg(err.response?.data?.error?.message ?? err.message ?? 'Eroare')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50/50">
      <h2 className="font-semibold text-slate-700">
        Open invoices (sold EA) — configurare & diagnostic
      </h2>
      <div className="flex flex-wrap gap-2 items-center">
        <input
          value={t1}
          onChange={(e) => setT1(e.target.value)}
          placeholder="token t1"
          className="px-3 py-2 rounded-md border border-slate-300 text-sm w-72 font-mono"
        />
        <input
          value={t2}
          onChange={(e) => setT2(e.target.value)}
          placeholder="token t2"
          className="px-3 py-2 rounded-md border border-slate-300 text-sm w-72 font-mono"
        />
        {canWrite && (
          <button
            onClick={saveTokens}
            disabled={busy || t1.length < 10 || t2.length < 10}
            className="px-4 py-2 rounded-md bg-slate-700 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
          >
            Salvează tokenii
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="filtru nume partener (opțional)"
          className="px-3 py-2 rounded-md border border-slate-300 text-sm w-72"
        />
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={summary} onChange={(e) => setSummary(e.target.checked)} />
          Sumar (aging pe partener)
        </label>
        <button
          onClick={() => runPreview(false)}
          disabled={busy}
          className="px-4 py-2 rounded-md border border-amber-500 text-amber-700 text-sm font-medium hover:bg-amber-50 disabled:opacity-50"
        >
          {busy ? 'Se încarcă…' : 'Diagnostic open_invoices'}
        </button>
        <button
          onClick={() => runPreview(true)}
          disabled={busy}
          className="px-4 py-2 rounded-md border border-slate-400 text-slate-600 text-sm font-medium hover:bg-slate-100 disabled:opacity-50"
        >
          Răspuns brut (debug)
        </button>
        {canWrite && (
          <button
            onClick={syncSold}
            disabled={busy}
            className="px-4 py-2 rounded-md bg-emerald-700 text-white text-sm font-medium hover:bg-emerald-800 disabled:opacity-50"
          >
            Sincronizează sold în CRM
          </button>
        )}
      </div>
      {msg && <div className="text-sm text-slate-600">{msg}</div>}
      {raw != null ? (
        <pre className="text-xs bg-slate-900 text-slate-100 rounded-md p-3 overflow-auto max-h-[50vh]">
          {JSON.stringify(raw, null, 2)}
        </pre>
      ) : null}
    </div>
  )
}
